// frontend/src/marketing/pages/VerifyPage.tsx
//
// Public, unauthenticated document-verification page. Anyone holding a
// signature number (printed on the sealed PDF, e.g. "WQ-2026-000123") can
// confirm it was genuinely completed on Worqly — no login required.
//
// Two entry points:
//   - /verify/:number — fetches on mount, shows a result panel.
//   - /verify          — a manual entry form (also shown above the result
//                        panel on the :number route, so a lookup can be
//                        re-run without going back).

import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Copy, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { esignPublicApi } from '../../modules/esign/api';
import type { VerifyResult } from '../../modules/esign/types';
import { MarketingPageHead } from '../MarketingPageHead';

type LookupState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'found'; result: VerifyResult }
  | { status: 'not_found' }
  | { status: 'error' };

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function VerifyPage() {
  const { number } = useParams<{ number: string }>();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState(number ?? '');
  const [state, setState] = React.useState<LookupState>({ status: 'idle' });

  const runLookup = React.useCallback((num: string) => {
    let cancelled = false;
    setState({ status: 'loading' });
    esignPublicApi.verify(num)
      .then((result) => {
        if (cancelled) return;
        setState({ status: 'found', result });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const httpStatus = (err as { status?: number } | null)?.status;
        setState({ status: httpStatus === 404 ? 'not_found' : 'error' });
      });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!number) {
      setState({ status: 'idle' });
      return;
    }
    return runLookup(number);
  }, [number, runLookup]);

  function onRetry() {
    if (!number) return;
    runLookup(number);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const normalized = query.trim().toUpperCase();
    if (!normalized) return;
    navigate(`/verify/${normalized}`);
  }

  async function copySha(sha: string) {
    try {
      await navigator.clipboard.writeText(sha);
      toast.success('SHA-256 fingerprint copied');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  }

  return (
    <section className="min-h-[70vh] py-16 lg:py-24 px-6">
      <MarketingPageHead
        title="Verify a signed document"
        description="Confirm a signature number issued by Worqly e-sign and check its SHA-256 fingerprint."
      />
      <div className="max-w-xl mx-auto">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-coral mb-3">Document verification</p>
        <h1 className="font-light text-hero-lg text-ink dark:text-white leading-tight tracking-[-0.03em] mb-4">
          Verify a signed document
        </h1>
        <p className="text-body-lg leading-relaxed text-ink/65 dark:text-white/65 text-pretty">
          Every agreement signed on Worqly receives an official signature number and a SHA-256
          fingerprint of the sealed PDF. To independently verify a file, compute its SHA-256 and
          compare.
        </p>

        <form onSubmit={onSubmit} className="mt-8 flex flex-col sm:flex-row gap-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="WQ-2026-000123"
            aria-label="Signature number"
            className="flex-1"
          />
          <Button type="submit" size="lg" disabled={!query.trim()}>
            <Search className="mr-1 h-4 w-4" />
            Verify
          </Button>
        </form>

        <div className="mt-8">
          {state.status === 'loading' && (
            <p className="text-[13px] text-ink/55 dark:text-white/55">Checking…</p>
          )}

          {state.status === 'found' && (
            <div className="rounded-2xl bg-lime-neon/15 dark:bg-lime-neon/10 ring-1 ring-lime-neon/40 px-6 py-6">
              <div className="flex items-center gap-2.5 mb-4">
                <CheckCircle2 className="h-5 w-5 text-coral-600" strokeWidth={2.5} />
                <h2 className="font-semibold text-[16px] text-ink dark:text-white">Genuine document</h2>
              </div>
              <dl className="space-y-3 text-[13.5px]">
                <div>
                  <dt className="text-ink/55 dark:text-white/55">Signed by</dt>
                  <dd className="text-ink dark:text-white font-medium">{state.result.tenant_display_name}</dd>
                </div>
                <div>
                  <dt className="text-ink/55 dark:text-white/55">Document title</dt>
                  <dd className="text-ink dark:text-white font-medium">{state.result.title}</dd>
                </div>
                <div>
                  <dt className="text-ink/55 dark:text-white/55">Completed</dt>
                  <dd className="text-ink dark:text-white font-medium">{formatDate(state.result.completed_at)}</dd>
                </div>
                <div>
                  <dt className="text-ink/55 dark:text-white/55">Signature number</dt>
                  <dd className="text-ink dark:text-white font-medium">{state.result.signature_number}</dd>
                </div>
                <div>
                  <dt className="text-ink/55 dark:text-white/55">SHA-256 fingerprint</dt>
                  <dd className="mt-1 flex items-start gap-2">
                    <code className="flex-1 break-all font-mono text-[12px] text-ink/85 dark:text-white/80 bg-white/60 dark:bg-white/5 rounded-lg px-2.5 py-1.5">
                      {state.result.sealed_sha256}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copySha(state.result.sealed_sha256)}
                      aria-label="Copy SHA-256 fingerprint"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </Button>
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {state.status === 'not_found' && (
            <div className="rounded-2xl bg-coral/10 ring-1 ring-coral/30 px-6 py-6">
              <div className="flex items-center gap-2.5 mb-2">
                <AlertTriangle className="h-5 w-5 text-coral-600" strokeWidth={2.5} />
                <h2 className="font-semibold text-[16px] text-ink dark:text-white">Not found</h2>
              </div>
              <p className="text-[13.5px] text-ink/70 dark:text-white/70">
                No document matches this signature number.
              </p>
            </div>
          )}

          {state.status === 'error' && (
            <div className="rounded-2xl bg-ink/5 dark:bg-white/5 ring-1 ring-ink/15 dark:ring-white/15 px-6 py-6">
              <div className="flex items-center gap-2.5 mb-2">
                <AlertTriangle className="h-5 w-5 text-ink/50 dark:text-white/50" strokeWidth={2.5} />
                <h2 className="font-semibold text-[16px] text-ink dark:text-white">Verification unavailable</h2>
              </div>
              <p className="text-[13.5px] text-ink/70 dark:text-white/70 mb-4">
                Verification is temporarily unavailable — please try again shortly.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
